import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/components/Icon";
import { fmtMoney } from "@/lib/money";
import PageContainer from "@/components/ui/PageContainer";
import SegmentedPills from "@/components/ui/SegmentedPills";
import { useDailyReward } from "@/lib/useDailyReward";
import { dailyRewardLabel, dailyRewardTerms, dailyRewardClaimMessage } from "@/lib/dailyReward";

/* PRYMEBET redesign — Rewards & VIP (design "05 Rewards & VIP", RW-01..04):
   Promotions (streak + offers grid), VIP Club (tier + claimable + tier ladder),
   Refer & Earn (code + share + stats + how-it-works), Streamer + Discord.
   Real data from user.vip + /referrals/me + /wallet/claim-bonus; unfinalized
   economics are honest "TBD" placeholders (as in the design itself). */

const DISCORD_URL = "https://discord.gg";

// Only offers that reflect real platform behavior. Fabricated "2 free bets in your
// slip" and "Odds Boost of the Day" tokens were removed (UIA-03): free bets are now
// shown ONLY when the account actually holds un-used tokens (built dynamically from
// user.free_bets below), and the odds boost is a labelled "Coming soon" card with no
// fake status or add-to-slip CTA.
// Only offers that reflect real, usable platform behavior get a live CTA; programs whose
// economics aren't finalized are honestly marked "Coming soon" with NO fabricated amount
// and NO add-to-slip CTA (PREMIUM_UI_DIRECTION §10 — hide unfinished economics, never fake
// a number). The daily claim + referral below are fully real today.
const OFFERS = [
  { key: "purchase", cat: "opted", eyebrow: "Purchase", icon: "promotions", title: "Coin packs", desc: "Pick a pack to play the sportsbook and casino.", meta: null, cta: "Get coins", to: "/store", status: "Available" },
  { key: "refer", cat: "opted", eyebrow: "Social", icon: "refer", title: "Refer a friend", desc: "Share your code — you're both rewarded when they place their first bet.", meta: null, cta: "Share code", to: "#refer", status: "Available" },
  { key: "welcome", cat: "sportsbook", eyebrow: "Sportsbook", icon: "sportsbook", title: "Welcome offer", desc: "A first-bet offer for new players, launching soon.", meta: null, cta: null, to: null, status: "Coming soon" },
  { key: "livecasino", cat: "livedealer", eyebrow: "Live dealer", icon: "livedealer", title: "Live Casino bonus", desc: "Bonus funds for Live Blackjack & Baccarat, launching soon.", meta: null, cta: null, to: null, status: "Coming soon" },
  { key: "oddsboost", cat: "sportsbook", eyebrow: "Daily", icon: "star", title: "Odds Boost of the Day", desc: "A featured boosted market, refreshed daily — coming soon.", meta: null, cta: null, to: null, status: "Coming soon" },
];

// Filters map to the cat values that actually exist on OFFERS (+ the dynamic free-bet card),
// so no tab ever resolves to an empty list.
const FILTERS = [
  { key: "all", label: "All offers" },
  { key: "opted", label: "Available" },
  { key: "sportsbook", label: "Sportsbook" },
  { key: "livedealer", label: "Live Dealer" },
];

const TIERS = [
  { name: "Bronze", wagered: "0", perks: ["Daily PCoins bonus", "Weekly boost", "Standard support"] },
  { name: "Silver", wagered: "1,000", perks: ["Daily PCoins reward", "Rakeback", "Weekly boost"] },
  { name: "Gold", wagered: "50,000", perks: ["Daily PCoins reward", "Rakeback", "Birthday bonus"] },
  { name: "Platinum", wagered: "150,000", perks: ["Daily PCoins reward", "Rakeback", "Level-up bonus"] },
  { name: "Diamond", wagered: "250,000", perks: ["Daily PCoins reward", "Rakeback", "Dedicated host"] },
];

const STATUS_STYLE = {
  "Ready": "bg-pnr-green/15 text-pnr-green",
  "Available": "bg-surface-5 text-meta",
  "Coming soon": "bg-surface-5 text-meta",
};


// §7 one-shot staggered entrance (reduced-motion guarded by the caller — static fallback).
const SECTION_STAGGER = { show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } } };
const SECTION_ITEM = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
};

export default function Rewards() {
  const { user, refresh, streamerMode, setStreamerMode } = useAuth();
  const reduceMotion = useReducedMotion();
  const [ref, setRef] = useState(null);
  const {
    bonus, loading: bonusLoading, error: bonusError, claiming,
    claim: claimReward, reload: loadBonus, countdown: liveCd, canClaim, onCooldown,
  } = useDailyReward();
  const [filter, setFilter] = useState("all");
  const [tip, setTip] = useState({ username: "", amount: "" });
  const [tipping, setTipping] = useState(false);
  useEffect(() => { api.get("/referrals/me").then(({ data }) => setRef(data)).catch(() => {}); }, []);

  const claim = async () => {
    try {
      const data = await claimReward();
      if (data) toast.success(dailyRewardClaimMessage(data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Come back later for your next reward.");
    }
  };

  const sendTip = async () => {
    if (!tip.username || !tip.amount) { toast.error("Enter a follower and amount"); return; }
    setTipping(true);
    try {
      await api.post("/tips", { to_username: tip.username, amount: Number(tip.amount), currency: "coins" });
      // Honor streamer mode: mask the amount in the confirmation toast (UIA-16).
      toast.success(`Sent ${fmtMoney(Number(tip.amount), "coins", { mask: streamerMode })} to ${tip.username}`);
      setTip({ username: "", amount: "" });
      refresh && refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send tip.");
    } finally { setTipping(false); }
  };

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/login?ref=${ref?.referral_code || ""}` : "";
  const shareText = `Join me on PRYMEBET — free-to-play sportsbook & casino. Use my code ${ref?.referral_code || ""}`;
  const share = (target) => {
    if (!ref?.referral_code) { toast.error("Referral code not ready yet"); return; }
    if (target === "x") window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener");
    else if (target === "discord") { navigator.clipboard.writeText(`${shareText} ${shareUrl}`); toast.success("Share text copied — paste it in Discord"); }
    else if (target === "sms") window.open(`sms:?&body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`, "_blank");
    else { navigator.clipboard.writeText(shareUrl); toast.success("Referral link copied"); }
  };

  const vip = user?.vip;

  // Real free-bet tokens held on the account drive a single truthful card (UIA-03).
  // No tokens => no card at all (never a fabricated "2 free bets in your slip").
  const freeBets = useMemo(() => (Array.isArray(user?.free_bets) ? user.free_bets : []), [user?.free_bets]);
  const allOffers = useMemo(() => {
    if (!freeBets.length) return OFFERS;
    const total = freeBets.reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const exp = freeBets.map((t) => t.expires_at).filter(Boolean).sort()[0];
    const n = freeBets.length;
    const fbCard = {
      key: "freebets", cat: "sportsbook", eyebrow: "Free bets", icon: "zap",
      title: `${n} free bet${n === 1 ? "" : "s"} in your slip`,
      desc: `${fmtMoney(total, "cash", { mask: streamerMode })} in free bets — you keep the profit in PCash.`,
      meta: exp ? `Expires ${new Date(exp).toLocaleDateString()}` : null,
      cta: "Use now", to: "/sportsbook", status: "Ready",
    };
    // Place free bets right after the welcome offer, matching the original layout.
    return [OFFERS[0], fbCard, ...OFFERS.slice(1)];
  }, [freeBets, streamerMode]);

  // "Available" = anything the player can act on right now (a real CTA/destination); this
  // includes the dynamic free-bet card when tokens are held. "Coming soon" cards are excluded.
  const isAvailable = (o) => o.status === "Available" || o.status === "Ready";
  const offers = useMemo(
    () => filter === "all" ? allOffers : allOffers.filter((o) => filter === "opted" ? isAvailable(o) : o.cat === filter),
    [filter, allOffers]
  );
  const availableCount = allOffers.filter(isAvailable).length;

  return (
    <PageContainer clearBottomNav rhythm={false} data-testid="rewards-page">
      {/* §7 one-shot staggered entrance — the page sections enter in sequence. Owns the page
          rhythm (space-y-10) so each section keeps its spacing. Reduced motion: static. */}
      <motion.div
        className="space-y-10"
        variants={reduceMotion ? undefined : SECTION_STAGGER}
        initial={reduceMotion ? false : "hidden"}
        animate="show"
      >
      <motion.div variants={reduceMotion ? undefined : SECTION_ITEM} className="pt-1">
        <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-pnr-green">Rewards</p>
        <h1 className="mt-1 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">Rewards &amp; VIP</h1>
      </motion.div>

      {/* ---------- PROMOTIONS ---------- */}
      <motion.section variants={reduceMotion ? undefined : SECTION_ITEM} id="daily" className="scroll-mt-24 space-y-5">
        {/* daily reward — gated on the real can_claim / next_claim_at (UIA-10) */}
        {(() => {
          // Real streak if the API ever exposes one; otherwise a neutral "start your
          // streak" state — never a fabricated day-5 position (UIA-03).
          const realStreak = Number(user?.vip?.daily_streak) || 0;
          return (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="surface-premium relative overflow-hidden rounded-2xl p-5">
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-pnr-green/[0.12] via-transparent to-transparent" />
              {/* Decorative coin cluster — brand texture; the real claim lives in the button. */}
              <div className="pointer-events-none absolute right-0 top-0 h-28 w-1/2 opacity-70">
                <span className="absolute right-6 top-5 flex h-20 w-20 items-center justify-center rounded-full bg-pnr-green/[0.07] ring-1 ring-pnr-green/20"><Icon name="pnrcoin" size={30} className="text-pnr-green" /></span>
                <span className="absolute right-2 top-2 h-9 w-9 rounded-full bg-pnr-green/[0.06] ring-1 ring-pnr-green/15" />
                <span className="absolute bottom-0 right-24 h-7 w-7 rounded-full bg-pnr-green/[0.06] ring-1 ring-pnr-green/15" />
              </div>
              <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="max-w-[72%]">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-pnr-green">Daily reward</p>
                  <h3 className="mt-1 font-display text-2xl font-black tracking-tight text-white">{canClaim ? "Today's reward" : "Next reward"}</h3>
                  <p className="mt-1 text-sm text-meta">
                    {bonusError ? "Unable to load your reward. Please retry." : bonus
                      ? <>{canClaim ? "Claim " : "Next reward: "}<b className="tabular-nums text-white">{dailyRewardLabel(bonus)}</b> every 24h</>
                      : "Loading your daily reward…"}
                    {realStreak > 0 ? <> · day {realStreak} streak</> : null}
                  </p>
                  {bonus && <p className="mt-1 text-xs text-meta">{dailyRewardTerms(bonus)}</p>}
                </div>
                {!onCooldown ? (
                  <button data-testid="rewards-claim" onClick={bonusError ? loadBonus : claim} disabled={claiming || bonusLoading || (!canClaim && !bonusError)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-pnr-green px-6 py-3 font-bold text-surface-1 shadow-[0_6px_18px_rgba(0,214,50,0.28)] transition-colors hover:bg-pnr-green-hover focus-ring disabled:cursor-not-allowed disabled:bg-pnr-green/40 disabled:text-surface-1/60 sm:w-auto">
                    <Icon name="pnrcoin" size={16} /> {bonusError ? "Retry reward" : bonusLoading ? "Loading…" : claiming ? "Claiming…" : "Claim today's reward"}
                  </button>
                ) : (
                  /* Claimed → live countdown REPLACES the Claim button (aligned with Home + Casino). */
                  <div data-testid="rewards-claim-countdown" aria-label={`Next reward in ${liveCd}`}
                    className="inline-flex w-full items-center gap-2 rounded-full border border-white/10 bg-black/40 px-5 py-2.5 sm:w-auto">
                    <Icon name="clock" size={16} className="text-pnr-green" />
                    <span className="flex flex-col leading-tight">
                      <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-meta">Next drop in</span>
                      <span className="font-display text-sm font-extrabold tabular-nums text-white">{liveCd || "soon"}</span>
                    </span>
                  </div>
                )}
              </div>
              {realStreak > 0 ? (
                <div className="relative mt-4 grid grid-cols-7 gap-2">
                  {[1, 2, 3, 4, 5, 6, 7].map((d) => {
                    const dayInWeek = ((realStreak - 1) % 7) + 1;
                    const today = d === dayInWeek;
                    const past = d < dayInWeek;
                    return (
                      <div key={d} className={`flex flex-col items-center gap-1.5 rounded-xl border p-2 ${today ? "border-pnr-green bg-pnr-green/10" : "border-surface-border bg-surface-4"}`}>
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${today ? "text-pnr-green" : "text-meta"}`}>{today ? "Today" : `Day ${d}`}</span>
                        <span className={`flex h-7 w-7 items-center justify-center rounded-full ${past ? "bg-pnr-green/20" : today ? "bg-pnr-green" : "bg-surface-5"}`}>
                          {past ? <Icon name="check-circle" size={14} className="text-pnr-green" /> : today ? <Icon name="pnrcoin" size={14} className="text-surface-1" /> : <span className="text-[10px] text-[#52525b]">—</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="relative mt-4 text-sm text-meta">Claim your daily reward to start a streak — consecutive days build it up.</p>
              )}
            </motion.div>
          );
        })()}

        {/* offers grid */}
        <div>
          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-display text-xl font-black tracking-tight text-white sm:text-2xl">Promotions</h2>
            {/* Category pills — white-selected (SegmentedPills) so they never compete with the
                single meaningful green (the claim/CTA); counts are real list lengths. */}
            <SegmentedPills
              className="sm:justify-end"
              size="sm"
              ariaLabel="Filter promotions"
              testidPrefix="rewards-filter"
              value={filter}
              onChange={setFilter}
              items={FILTERS.map((f) => ({
                id: f.key,
                label: `${f.label}${f.key === "all" ? ` ${allOffers.length}` : f.key === "opted" ? ` ${availableCount}` : ""}`,
              }))}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {offers.map((o) => (
              <div key={o.key} className="surface-premium flex flex-col gap-3 rounded-2xl p-5">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-[0.16em] text-meta">
                    <Icon name={o.icon} size={15} className="text-pnr-green" /> {o.eyebrow}
                  </span>
                  {o.status && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLE[o.status] || "bg-surface-5 text-meta"}`}>{o.status}</span>}
                </div>
                <div>
                  <h3 className="font-display text-base font-black tracking-tight text-white">{o.title}</h3>
                  <p className="mt-1 text-sm text-meta">{o.desc}</p>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <span className="text-xs text-meta">{o.meta || ""}</span>
                  {o.cta && o.to ? (
                    <Link to={o.to} className="inline-flex items-center gap-1 rounded-full text-sm font-bold text-pnr-green hover:text-pnr-green-hover focus-ring">{o.cta} <Icon name="arrow-right" size={14} /></Link>
                  ) : (
                    <span className="text-xs font-semibold text-meta">Not yet available</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ---------- VIP CLUB ---------- */}
      <motion.section variants={reduceMotion ? undefined : SECTION_ITEM} id="vip" className="scroll-mt-24 space-y-4">
        <h2 className="font-display text-xl font-black tracking-tight text-white sm:text-2xl">VIP Club</h2>
        {vip && (() => {
          // TRUTHFUL progress: only treat progress as known when the API returns a finite
          // number. Otherwise show a neutral "unavailable" state — never a fabricated 0%/bar.
          const rawProgress = Number(vip.progress);
          const hasProgress = vip.next_tier && Number.isFinite(rawProgress);
          const pct = hasProgress ? Math.max(0, Math.min(100, rawProgress)) : 0;
          return (
          <div className="surface-premium rounded-2xl p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-pnr-green/12"><Icon name="vip-fill" size={24} className="text-pnr-green" /></span>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-meta">Your tier</p>
                  <h3 className="font-display text-2xl font-black tracking-tight text-white">{vip.tier} · Level {vip.level}</h3>
                  <p className="text-sm text-meta">
                    Wagered <span className="tabular-nums text-white">{Number(vip.total_wagered || 0).toLocaleString()}</span>
                    {vip.next_tier ? (hasProgress ? <> · <span className="tabular-nums">{pct}%</span> to {vip.next_tier}</> : <> · next tier {vip.next_tier}</>) : null}
                  </p>
                </div>
              </div>
              {/* tier stat row — only real values; unfinalized economics read a neutral em dash,
                  never a fabricated number. */}
              <div className="grid grid-cols-3 gap-2 lg:min-w-[360px]">
                {[["Daily PCoins", bonus?.amount != null ? Number(bonus.amount).toLocaleString() : "—", true], ["Rakeback", "—", false], ["Weekly boost", "Ready", false]].map(([l, v, num]) => (
                  <div key={l} className="rounded-xl border border-surface-border bg-surface-5 px-3 py-2.5 text-center">
                    <div className={`font-display text-base font-black text-white ${num ? "tabular-nums" : ""}`}>{v}</div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-meta">{l}</div>
                  </div>
                ))}
              </div>
            </div>
            {hasProgress && (
              <div className="mt-4">
                <div className="h-2 overflow-hidden rounded-full bg-surface-border"><div className="h-full rounded-full bg-pnr-green" style={{ width: `${pct}%` }} /></div>
              </div>
            )}
            {/* VIP perks — economics not finalized yet, so these are honestly not claimable (UIA-03) */}
            <div className="mt-5">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-meta">VIP perks</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {[["Rakeback", "Rate not set yet"], ["Weekly boost", "Resets Mon 00:00 ET"], ["Level-up bonus", "On next level"]].map(([label, note]) => (
                  <div key={label} className="flex items-center justify-between rounded-xl border border-surface-border bg-surface-5 px-3.5 py-2.5">
                    <div><div className="text-sm font-semibold text-white">{label}</div><div className="text-[11px] text-meta">{note}</div></div>
                    <span title="Available once VIP economics are finalized" className="rounded-full border border-surface-border px-3 py-1.5 text-xs font-bold text-meta">Soon</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          );
        })()}
        {/* tier ladder */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {TIERS.map((t) => {
            const current = vip?.tier === t.name;
            return (
              <div key={t.name} className={`rounded-2xl p-4 ${current ? "border border-pnr-green/50 bg-pnr-green/[0.06]" : "surface-premium"}`}>
                <div className="flex items-center justify-between">
                  <span className={`font-display text-sm font-black ${current ? "text-pnr-green" : "text-white"}`}>{t.name}</span>
                  {current && <span className="rounded-full bg-pnr-green px-2 py-0.5 text-[10px] font-bold text-surface-1">Current</span>}
                </div>
                <p className="mt-0.5 text-[11px] text-meta"><span className="tabular-nums">{t.wagered}</span> wagered</p>
                <ul className="mt-3 space-y-1.5">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-1.5 text-xs text-meta"><Icon name="check-circle" size={13} className="mt-0.5 shrink-0 text-pnr-green" /><span>{p}</span></li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </motion.section>

      {/* ---------- REFER & EARN ---------- */}
      <motion.section variants={reduceMotion ? undefined : SECTION_ITEM} id="refer" className="surface-premium scroll-mt-24 rounded-2xl p-5">
        <div className="flex items-center gap-2"><Icon name="refer" size={18} className="text-pnr-green" /><h2 className="font-display text-xl font-black tracking-tight text-white sm:text-2xl">Refer &amp; earn</h2></div>
        <p className="mt-1 text-sm text-meta">Friends enter your code at sign-up. You're rewarded when they place their first bet.</p>
        <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3 rounded-xl border border-dashed border-pnr-green/40 bg-pnr-green/[0.05] px-4 py-3">
            <span className="font-display text-2xl font-black tracking-widest text-white" data-testid="rewards-referral-code">{ref?.referral_code || "—"}</span>
            <button onClick={() => { if (ref?.referral_code) { navigator.clipboard.writeText(ref.referral_code); toast.success("Referral code copied"); } }}
              aria-label="Copy referral code"
              className="inline-flex items-center gap-1 rounded-full bg-surface-5 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-surface-4 focus-ring"><Icon name="copy" size={13} /> Copy</button>
          </div>
          {/* Real referral counts (0/0 when none) — never fabricated; "Earned" shows an em dash
              until the program credits a real amount. */}
          <div className="grid grid-cols-3 gap-3">
            {[["Signed up", ref?.count ?? 0, true], ["Qualified", ref?.qualified ?? 0, true], ["Earned", ref?.earned != null ? `${ref.earned}` : "—", ref?.earned != null]].map(([l, v, num]) => (
              <div key={l} className="rounded-xl border border-surface-border bg-surface-5 px-4 py-3 text-center">
                <div className={`font-display text-xl font-black text-white ${num ? "tabular-nums" : ""}`}>{v}</div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-meta">{l}</div>
              </div>
            ))}
          </div>
        </div>
        {/* share targets */}
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[["x", "X / Twitter"], ["discord", "Discord"], ["sms", "SMS"], ["link", "Link"]].map(([k, l]) => (
            <button key={k} onClick={() => share(k)}
              className="flex h-11 items-center justify-center rounded-xl border border-surface-border bg-surface-5 text-xs font-bold text-white transition-colors hover:border-border-strong hover:bg-surface-4 focus-ring">
              {l}
            </button>
          ))}
        </div>
        {/* how it works */}
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[["01", "Share your code", "Anywhere you hang out."], ["02", "Friend signs up", "They enter your code at sign-up."], ["03", "First bet placed", "You both get a reward."]].map(([n, t, d]) => (
            <div key={n} className="rounded-xl border border-surface-border bg-surface-5 p-4">
              <div className="font-display text-lg font-black tabular-nums text-pnr-green">{n}</div>
              <div className="mt-1 text-sm font-bold text-white">{t}</div>
              <div className="text-xs text-meta">{d}</div>
            </div>
          ))}
        </div>
        {ref?.referred?.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-meta">Your referrals</p>
            <div className="space-y-1.5">
              {ref.referred.slice(0, 6).map((r, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-surface-border bg-surface-5 px-3 py-2 text-sm">
                  <span className="text-white">{r.username || r.email || "Player"}</span>
                  <span className="text-xs font-semibold text-pnr-green">{r.qualified ? "Qualified" : "Signed up"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.section>

      {/* ---------- STREAMER + DISCORD ---------- */}
      <motion.div variants={reduceMotion ? undefined : SECTION_ITEM} className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <section className="surface-premium rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Icon name="live" size={18} className="text-pnr-green" /><h2 className="font-display text-lg font-black tracking-tight text-white">Streamer mode</h2></div>
            <button type="button" onClick={() => setStreamerMode(!streamerMode)} role="switch" aria-checked={streamerMode}
              aria-label={`Streamer mode ${streamerMode ? "on" : "off"} — masks balances app-wide`}
              className={`relative h-7 w-12 rounded-full transition-colors focus-ring ${streamerMode ? "bg-pnr-green" : "bg-surface-5"}`}>
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${streamerMode ? "left-6" : "left-1"}`} />
            </button>
          </div>
          <p className="mt-2 text-sm text-meta">Masks your balances app-wide while you're live.</p>
          {/* tip a follower */}
          <div className="mt-4 space-y-2 rounded-xl border border-surface-border bg-surface-5 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-meta">Tip a follower</p>
            <label htmlFor="tip_username" className="sr-only">Follower username</label>
            <input id="tip_username" value={tip.username} onChange={(e) => setTip((t) => ({ ...t, username: e.target.value }))} name="tip_username" placeholder="their exact username"
              className="w-full rounded-lg border border-surface-border bg-surface-4 px-3 py-2 text-sm text-white outline-none transition-colors placeholder:text-[#52525b] focus:border-pnr-green/50 focus-ring" />
            <div className="flex gap-2">
              <label htmlFor="tip_amount" className="sr-only">Tip amount in PCoins</label>
              <input id="tip_amount" value={tip.amount} onChange={(e) => setTip((t) => ({ ...t, amount: e.target.value.replace(/[^0-9]/g, "") }))} name="tip_amount" inputMode="numeric" placeholder="500"
                className="w-28 rounded-lg border border-surface-border bg-surface-4 px-3 py-2 text-sm tabular-nums text-white outline-none transition-colors placeholder:text-[#52525b] focus:border-pnr-green/50 focus-ring" />
              <button type="button" onClick={sendTip} disabled={tipping} className="flex-1 rounded-lg bg-pnr-green py-2 text-sm font-bold text-surface-1 transition-colors hover:bg-pnr-green-hover focus-ring disabled:cursor-not-allowed disabled:bg-pnr-green/40 disabled:text-surface-1/60">
                {tipping ? "Sending…" : "Send PCoins"}
              </button>
            </div>
          </div>
        </section>

        <a href={DISCORD_URL} target="_blank" rel="noreferrer" className="surface-premium group flex flex-col justify-between rounded-2xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[var(--elev-3)] focus-ring">
          <div className="flex items-center gap-2"><Icon name="support" size={18} className="text-pnr-green" /><h2 className="font-display text-lg font-black tracking-tight text-white">Discord</h2></div>
          <p className="mt-2 text-sm text-meta">Community, drops and support — anywhere you hang out.</p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-white group-hover:text-pnr-green">Join the server <Icon name="arrow-right" size={14} /></span>
        </a>
      </motion.div>
      </motion.div>
    </PageContainer>
  );
}

